import React from 'react';
import { EmailMessageSummary } from '../types';
import EmailMessageActionsTab, { EmailMessageAction } from './EmailMessageActionsTab';

interface EmailMessageListProps {
  messages: EmailMessageSummary[];
  loading: boolean;
  syncing?: boolean;
  error: string | null;
  selectedUid: number | null;
  selectedMailbox?: string | null;
  onSelectMessage: (message: EmailMessageSummary) => void;
  onMessageAction?: (action: EmailMessageAction, message: EmailMessageSummary) => void;
  emptyHint?: string;
}

function formatEmailDate(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return isoDate;
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();

  if (isToday) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

const EmailMessageList: React.FC<EmailMessageListProps> = ({
  messages,
  loading,
  syncing = false,
  error,
  selectedUid,
  selectedMailbox = null,
  onSelectMessage,
  onMessageAction,
  emptyHint
}) => {
  if (loading && messages.length === 0) {
    return <div className="email-list-state">Loading messages...</div>;
  }

  if (error && messages.length === 0) {
    return <div className="email-list-state email-list-error">{error}</div>;
  }

  if (messages.length === 0) {
    return (
      <div className="email-list-state">
        {syncing ? 'Syncing from mailbox…' : (emptyHint ?? 'No messages stored. Click Sync to fetch from mailbox.')}
      </div>
    );
  }

  return (
    <div className={`email-message-list${loading || syncing ? ' email-message-list--refreshing' : ''}`}>
      {messages.map(msg => {
        const isSelected = selectedUid === msg.uid
          && (selectedMailbox == null || selectedMailbox === msg.mailbox);

        return (
          <div key={msg.uid} className={`email-message-row-wrapper${isSelected ? ' active' : ''}`}>
            {isSelected && onMessageAction && (
              <EmailMessageActionsTab
                isRead={msg.isRead}
                onAction={(action) => onMessageAction(action, msg)}
              />
            )}
            <div
              className={`email-message-row${isSelected ? ' active' : ''}${!msg.isRead ? ' unread' : ''}`}
              onClick={() => onSelectMessage(msg)}
            >
              <div className="email-message-meta">
                {!msg.isRead && <span className="email-message-unread-dot" aria-hidden="true" />}
                <span className="email-message-date">{formatEmailDate(msg.date)}</span>
              </div>
              <div className="email-message-from">{msg.from}</div>
              <div className="email-message-subject">{msg.subject}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default EmailMessageList;
